// src/pages/InventoryPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { inventoryAPI, productsAPI, companiesAPI } from '../services/api';
import { Card, Badge, SectionHeader, EmptyState, Button, Modal, Input } from '../components/ui';
import { format, fromUnixTime } from 'date-fns';
import toast from 'react-hot-toast';
import { Boxes, Search, Filter, Scale, ChevronDown, ChevronUp, Clock } from 'lucide-react';
import styles from './InventoryPage.module.css';

const STATUS_FILTERS = ['ALL', 'IN_STOCK', 'DEPARTED', 'ENCODED'];

export default function InventoryPage() {
    const { companyId, isGaafAdmin } = useAuth();
    const [inventory, setInventory] = useState([]);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('IN_STOCK');
    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState({});
    const [weightModal, setWeightModal] = useState(null);
    const [activeTab, setActiveTab] = useState('inventory');
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');

    useEffect(() => {
        if (!isGaafAdmin) return;
        companiesAPI.list().then(res => {
            const list = res.data.companies || [];
            setCompanies(list);
            if (list.length > 0) setSelectedCompanyId(list[0].companyId);
        }).catch(() => { });
    }, [isGaafAdmin]);

    const effectiveCompanyId = isGaafAdmin ? selectedCompanyId : companyId;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const baseParams = effectiveCompanyId ? { companyId: effectiveCompanyId } : {};
            const statusParam = statusFilter === 'ALL' ? {} : { status: statusFilter };

            const [invRes, evRes, prodRes] = await Promise.all([
                inventoryAPI.get({ ...baseParams, ...statusParam }),
                inventoryAPI.getEvents({ ...baseParams, limit: 100 }),
                productsAPI.list(effectiveCompanyId || undefined),
            ]);

            const skuMap = {};
            (prodRes.data.products || []).forEach(p => { skuMap[p.productId] = p.sku; });

            let inv = invRes.data.inventory || [];

            if (statusFilter === 'DEPARTED' && inv.length === 0) {
                const evs = evRes.data.events || [];
                const departedEvs = evs.filter(e => e.eventType === 'DEPARTED');
                const byProduct = {};
                departedEvs.forEach(ev => {
                    if (!byProduct[ev.productId]) {
                        byProduct[ev.productId] = {
                            productId: ev.productId, productName: ev.productName,
                            productSku: skuMap[ev.productId] || '', trackingType: 'UNIT',
                            inStock: 0, departed: 0, totalTags: 0, _displayCount: 0,
                            calculations: { summary: '' }, isLowStock: false,
                        };
                    }
                    byProduct[ev.productId].departed++;
                    byProduct[ev.productId].totalTags++;
                    byProduct[ev.productId]._displayCount++;
                    byProduct[ev.productId].calculations.summary = `${byProduct[ev.productId].departed} departed`;
                });
                inv = Object.values(byProduct);
            }

            if (statusFilter !== 'IN_STOCK' && statusFilter !== 'ALL') {
                inv.forEach(item => {
                    if (!item._displayCount) item._displayCount = item.totalTags || item.departed || item.inStock || 0;
                });
            }

            setInventory(inv);
            setEvents(evRes.data.events || []);
        } catch (_) { }
        setLoading(false);
    }, [effectiveCompanyId, statusFilter]);

    useEffect(() => { load(); }, [load]);

    const filtered = inventory.filter((item) =>
        !search ||
        item.productName?.toLowerCase().includes(search.toLowerCase()) ||
        item.productSku?.toLowerCase().includes(search.toLowerCase())
    );

    const [productItems, setProductItems] = useState({});

    const toggle = async (productId) => {
        setExpanded((p) => ({ ...p, [productId]: !p[productId] }));
        if (!productItems[productId]) {
            try {
                const params = {
                    ...(effectiveCompanyId ? { companyId: effectiveCompanyId } : {}),
                    productId,
                    status: statusFilter === 'ALL' ? undefined : statusFilter,
                };
                const res = await inventoryAPI.get(params);
                const itemsRes = await inventoryAPI.getEvents({
                    ...(effectiveCompanyId ? { companyId: effectiveCompanyId } : {}),
                    productId, limit: 100,
                });
                const seen = new Set();
                const items = [];
                (itemsRes.data.events || []).forEach(ev => {
                    if (!seen.has(ev.itemId)) { seen.add(ev.itemId); items.push({ itemId: ev.itemId, batchNumber: null }); }
                });
                setProductItems(p => ({ ...p, [productId]: items }));
            } catch (_) { }
        }
    };

    return (
        <div className={`${styles.page} animate-fadeIn`}>
            <SectionHeader
                title="Inventory"
                description="Real-time RFID Inventory with Packaging Math"
                actions={
                    isGaafAdmin && companies.length > 0 ? (
                        <select value={selectedCompanyId} onChange={e => setSelectedCompanyId(e.target.value)}
                            style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--text-primary)', padding: '7px 12px', fontFamily: 'var(--font-body)', fontSize: '0.875rem', outline: 'none' }}>
                            {companies.map(c => <option key={c.companyId} value={c.companyId}>{c.name}</option>)}
                        </select>
                    ) : null
                }
            />

            <div className={styles.tabs}>
                <button className={`${styles.tab} ${activeTab === 'inventory' ? styles.tabActive : ''}`} onClick={() => setActiveTab('inventory')}>
                    <Boxes size={15} /> Products
                </button>
                <button className={`${styles.tab} ${activeTab === 'events' ? styles.tabActive : ''}`} onClick={() => setActiveTab('events')}>
                    <Clock size={15} /> Movement History
                </button>
            </div>

            {activeTab === 'inventory' ? (
                <>
                    <div className={styles.toolbar}>
                        <div className={styles.searchWrap}>
                            <Search size={14} className={styles.searchIcon} />
                            <input className={styles.searchInput} placeholder="Search Products..." value={search} onChange={(e) => setSearch(e.target.value)} />
                        </div>
                        <div className={styles.statusFilters}>
                            {STATUS_FILTERS.map((s) => (
                                <button key={s} className={`${styles.filterBtn} ${statusFilter === s ? styles.filterActive : ''}`} onClick={() => setStatusFilter(s)}>
                                    {s === 'ALL' ? 'All' : s.replace('_', ' ')}
                                </button>
                            ))}
                        </div>
                    </div>

                    {loading ? (
                        <div className={styles.cardGrid}>{[1, 2, 3].map(i => <SkeletonCard key={i} />)}</div>
                    ) : filtered.length === 0 ? (
                        <EmptyState icon={<Boxes size={40} />} title="No Inventory found"
                            description={statusFilter === 'IN_STOCK' ? "Send Tag reads via the Gateway to see Items appear here." : "No Items match this filter."} />
                    ) : (
                        <div className={styles.cardGrid}>
                            {filtered.map((item) => (
                                <InventoryCard key={item.productId} item={item} expanded={expanded[item.productId]}
                                    fetchedItems={productItems[item.productId]} onToggle={() => toggle(item.productId)}
                                    onUpdateWeight={(epc) => setWeightModal(epc)} />
                            ))}
                        </div>
                    )}
                </>
            ) : (
                <EventsTable events={events} loading={loading} />
            )}

            <WeightModal open={!!weightModal} itemId={weightModal} onClose={() => setWeightModal(null)} onSaved={() => { setWeightModal(null); load(); }} />
        </div>
    );
}

// ── Inventory Card ─────────────────────────────────────────────────────────────
function InventoryCard({ item, expanded, fetchedItems, onToggle, onUpdateWeight }) {
    const calc = item.calculations || {};
    const hasMerma = item.trackingType === 'WEIGHT' && (item.trackMerma || calc.hasMerma);
    const tagItems = fetchedItems || item.items || calc.items || [];

    return (
        <Card className={styles.invCard} hover>
            <div className={styles.invCardHeader} onClick={onToggle}>
                <div className={styles.invCardLeft}>
                    <div className={styles.invCardName}>{item.productName || '—'}</div>
                    <div className={styles.invCardSku}>{item.productSku || item.sku || item.productId}</div>
                </div>
                <div className={styles.invCardRight}>
                    <div className={styles.invCardCount}>{item._displayCount ?? item.inStock}</div>
                    <div className={styles.invCardCountLabel}>{item._displayCount != null && item.inStock === 0 ? 'labels' : 'in stock'}</div>
                    {expanded ? <ChevronUp size={14} className={styles.chevron} /> : <ChevronDown size={14} className={styles.chevron} />}
                </div>
            </div>
            <div className={styles.summaryLine}>
                <span className={styles.summaryText}>
                    {item._displayCount != null && item.inStock === 0
                        ? (item.departed ? `${item.departed} item${item.departed !== 1 ? 's' : ''} Departed from Warehouse` : `${item._displayCount} label${item._displayCount !== 1 ? 's' : ''} Encoded — not yet received into Warehouse`)
                        : calc.summary || '—'}
                </span>
                {item.isLowStock && <Badge variant="warning">Low Stock</Badge>}
            </div>
            {hasMerma && calc.totalMerma !== 0 && (
                <div className={`${styles.mermaBar} ${calc.totalMerma > 0 ? styles.mermaLoss : styles.mermaGain}`}>
                    <Scale size={12} /><span>{calc.mermaNote}</span>
                </div>
            )}
            {expanded && calc.packaging && (
                <div className={styles.packaging}>
                    <div className={styles.packagingTitle}>Packaging Breakdown</div>
                    <div className={styles.packagingGrid}>
                        {calc.packaging.breakdown?.filter(b => b.count > 0).map((level, i) => (
                            <div key={i} className={styles.packagingLevel}>
                                <span className={styles.packagingCount}>{level.count}</span>
                                <span className={styles.packagingName}>{level.level}{level.count !== 1 ? 's' : ''}</span>
                                <span className={styles.packagingUnits}>({level.totalUnits} units)</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {expanded && item.forecast?.available && (
                <div className={styles.forecast}>
                    <div className={styles.forecastLabel}>Forecast</div>
                    <div className={styles.forecastText}>{item.forecast.forecastNote}</div>
                    {item.forecast.estimatedDepletionDate && <div className={styles.forecastDate}>Estimated depletion: <strong>{item.forecast.estimatedDepletionDate}</strong></div>}
                </div>
            )}
            {expanded && (
                <div className={styles.itemsList}>
                    <div className={styles.itemsHeader}>
                        Individual Tags
                        {tagItems.length === 0 && <span style={{ fontWeight: 400, color: 'var(--text-tertiary)', marginLeft: 8 }}>— expand after encoding tags for this product</span>}
                    </div>
                    {tagItems.slice(0, 10).map((tag) => (
                        <div key={tag.itemId} className={styles.tagRow}>
                            <code className={styles.tagEpc}>{tag.itemId?.slice(-12)}</code>
                            <span className={styles.tagBatch}>{tag.batch || tag.batchNumber || '—'}</span>
                            {hasMerma && <button className={styles.weightBtn} onClick={() => onUpdateWeight(tag.itemId)}><Scale size={12} /> Update Weight</button>}
                        </div>
                    ))}
                    {tagItems.length > 10 && <div className={styles.moreItems}>+{tagItems.length - 10} more tags</div>}
                </div>
            )}
        </Card>
    );
}

// ── Events Table — now with search ────────────────────────────────────────────
function EventsTable({ events, loading }) {
    const [search, setSearch] = useState('');

    const filtered = events.filter(ev =>
        !search ||
        ev.productName?.toLowerCase().includes(search.toLowerCase()) ||
        ev.eventType?.toLowerCase().includes(search.toLowerCase()) ||
        ev.itemId?.toLowerCase().includes(search.toLowerCase()) ||
        ev.reader?.toLowerCase().includes(search.toLowerCase())
    );

    if (loading) return <div className={styles.tableLoading}><div className="skeleton" style={{ height: 200 }} /></div>;
    if (events.length === 0) return (
        <EmptyState icon={<Clock size={40} />} title="No Movement Events yet" description="Events appear here as Tags pass through your RFID Portal." />
    );

    return (
        <>
            <div style={{ position: 'relative', maxWidth: 360, marginBottom: 14 }}>
                <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', pointerEvents: 'none' }} />
                <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search by Product, Event, Reader…"
                    style={{ width: '100%', paddingLeft: 34, paddingRight: 12, paddingTop: 8, paddingBottom: 8, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', color: 'var(--text-primary)', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' }}
                />
            </div>
            <Card padding={false}>
                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Product</th><th>Event</th><th>Direction</th><th>Reader</th><th>Time</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr><td colSpan={5} style={{ textAlign: 'center', padding: '24px', color: 'var(--text-tertiary)' }}>No Events match your search</td></tr>
                            ) : filtered.map((ev) => (
                                <tr key={ev.eventId}>
                                    <td className={styles.tdProduct}>
                                        <span>{ev.productName || '—'}</span>
                                        <code className={styles.epcSmall}>{ev.itemId?.slice(-10)}</code>
                                    </td>
                                    <td>
                                        <Badge variant={ev.eventType === 'ENTERED' ? 'success' : ev.eventType === 'DEPARTED' ? 'warning' : 'neutral'}>{ev.eventType}</Badge>
                                    </td>
                                    <td className={styles.tdDir}>{ev.direction === 'ENTERING' ? '→ In' : ev.direction === 'EXITING' ? '← Out' : ev.direction || '—'}</td>
                                    <td><code style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{ev.reader || '—'}</code></td>
                                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: 'var(--text-tertiary)' }}>
                                        {ev.timestamp ? format(fromUnixTime(ev.timestamp), 'MM/dd HH:mm:ss') : '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>
        </>
    );
}

// ── Weight Modal ────────────────────────────────────────────────────────────────
function WeightModal({ open, itemId, onClose, onSaved }) {
    const [weight, setWeight] = useState('');
    const [unit, setUnit] = useState('quintal');
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) { setWeight(''); setUnit('quintal'); setNotes(''); setLoading(false); }
    }, [open, itemId]);

    const handleSave = async () => {
        if (!weight) return toast.error('Enter Current Weight');
        setLoading(true);
        try {
            await inventoryAPI.updateWeight(itemId, { currentWeight: parseFloat(weight), unit, notes });
            toast.success('Weight Updated');
            onSaved();
        } catch (err) { toast.error(err?.response?.data?.message || 'Failed to Update Weight'); }
        setLoading(false);
    };

    return (
        <Modal open={open} onClose={onClose} title="Update Weight (Merma Tracking)">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-tertiary)' }}>Record the current physical weight of this tag. The system will calculate moisture loss (merma) automatically.</p>
                <Input label="Current Weight" type="number" step="0.001" min="0" placeholder="0.85" value={weight} onChange={(e) => setWeight(e.target.value)} />
                <div>
                    <label style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Unit</label>
                    <select value={unit} onChange={(e) => setUnit(e.target.value)} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--text-primary)', padding: '9px 12px', fontFamily: 'var(--font-body)', fontSize: '0.875rem', width: '100%' }}>
                        <option value="quintal">Quintal</option>
                        <option value="kg">Kilogram (kg)</option>
                        <option value="lb">Pound (lb)</option>
                    </select>
                </div>
                <Input label="Notes (optional)" placeholder="e.g. Weighed after 3 days storage" value={notes} onChange={(e) => setNotes(e.target.value)} />
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" loading={loading} onClick={handleSave}>Save Weight</Button>
                </div>
            </div>
        </Modal>
    );
}

function SkeletonCard() {
    return (
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: 20 }}>
            <div className="skeleton" style={{ height: 18, width: '60%', marginBottom: 8 }} />
            <div className="skeleton" style={{ height: 12, width: '40%', marginBottom: 16 }} />
            <div className="skeleton" style={{ height: 24, width: '30%' }} />
        </div>
    );
}
